import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { urlConfig } from '../config/urlConfig.js';

const GetStarted = () => {
  const navigate = useNavigate();
  const [webCarouselIndex, setWebCarouselIndex] = useState(0);
  const [mobileCarouselIndex, setMobileCarouselIndex] = useState(0);
  const [fullscreenImage, setFullscreenImage] = useState(null);
  const [fullscreenImages, setFullscreenImages] = useState([]);
  const [fullscreenIndex, setFullscreenIndex] = useState(0);

  const handleIOSDownload = () => {
    window.open('https://apps.apple.com/us/app/andlegur/id6759088599', '_blank');
  };

  const handleAndroidDownload = () => {
    window.open('https://play.google.com/store/apps/details?id=com.andlegur.mobile', '_blank');
  };

  // Fullscreen image handlers
  const openFullscreen = (images, index) => {
    setFullscreenImages(images);
    setFullscreenIndex(index);
    setFullscreenImage(images[index]);
    document.body.style.overflow = 'hidden'; // Prevent background scroll
  };

  const closeFullscreen = () => {
    setFullscreenImage(null);
    setFullscreenImages([]);
    setFullscreenIndex(0);
    document.body.style.overflow = 'auto'; // Restore scroll
  };

  const nextFullscreenImage = () => {
    const newIndex = (fullscreenIndex + 1) % fullscreenImages.length;
    setFullscreenIndex(newIndex);
    setFullscreenImage(fullscreenImages[newIndex]);
  };

  const prevFullscreenImage = () => {
    const newIndex = (fullscreenIndex - 1 + fullscreenImages.length) % fullscreenImages.length;
    setFullscreenIndex(newIndex);
    setFullscreenImage(fullscreenImages[newIndex]);
  };

  // Escape key handler for fullscreen
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && fullscreenImage) {
        closeFullscreen();
      }
    };

    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [fullscreenImage]);

  // Web app images
  const webAppImages = [
    { src: '/webb_app_images/Sc 1.webp', alt: 'Web App Dashboard' },
    { src: '/webb_app_images/Sc 2.webp', alt: 'Web App Insights' },
    { src: '/webb_app_images/SC 3.webp', alt: 'Web App Guided Practice' },
    { src: '/webb_app_images/Sc 4.webp', alt: 'Web App Practice' },
    { src: '/webb_app_images/SC 5.webp', alt: 'Web App Journals' },
    { src: '/webb_app_images/Sc 6 (1).webp', alt: 'Web App Chat' },
    { src: '/webb_app_images/SC 7.webp', alt: 'Web App Community' },
    { src: '/webb_app_images/Sc 8.webp', alt: 'Web App Shop' },
    { src: '/webb_app_images/SC 9.webp', alt: 'Web App Settings' },
    { src: '/webb_app_images/SC 10.webp', alt: 'Web App Membership' },
    { src: '/webb_app_images/SC 11.webp', alt: 'Web App Welcome' },
    { src: '/webb_app_images/Sc 12.webp', alt: 'Web App login' },
    { src: '/webb_app_images/SC 13.webp', alt: 'Web App Register' }
  ];

  // Mobile app images
  const mobileAppImages = [
    { src: '/mobile_app_images/Sr shot1.webp', alt: 'Mobile App Home' },
    { src: '/mobile_app_images/Sr shot2.webp', alt: 'Mobile App Conversations' },
    { src: '/mobile_app_images/Sr shot3.webp', alt: 'Mobile App Journaling' },
    { src: '/mobile_app_images/Sr shot 4.webp', alt: 'Mobile App Emotional Analytics' },
    { src: '/mobile_app_images/Sr shot5.webp', alt: 'Mobile App Guided Practices' },
    { src: '/mobile_app_images/Sr shot 6.webp', alt: 'Mobile App Archetypes' },
    { src: '/mobile_app_images/Sr shot7.webp', alt: 'Mobile App Shop' },
    { src: '/mobile_app_images/Sr 8.webp', alt: 'Mobile App Conversations' },
    { src: '/mobile_app_images/Sr shot 9.webp', alt: 'Mobile App Community' },
    { src: '/mobile_app_images/S1.jpg', alt: 'Mobile App Welcome' },
    { src: '/mobile_app_images/S2.jpg', alt: 'Mobile App Login' },
    { src: '/mobile_app_images/S6.jpg', alt: 'Mobile App Home screen' },
    { src: '/mobile_app_images/S11.jpg', alt: 'Mobile App Luna Insights' },
    { src: '/mobile_app_images/S14.jpg', alt: 'Mobile App Personal Insights ' }
  ];

  // Platform cards data
  const platforms = [
    {
      name: 'Web App',
      icon: (
        <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
      ),
      description: 'Access Andlegur from any browser on phone, laptop or tablet',
      features: [
        'Full-featured experience',
        'Works on all modern browsers',
        'Automatic updates',
        'Cross-platform sync'
      ],
      action: 'Launch Web App',
      onClick: () => navigate('/login'),
      gradient: 'from-blue-600 to-indigo-700'
    },
    {
      name: 'iOS App',
      icon: (
        <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
        </svg>
      ),
      description: 'Download on the App Store for iPhone & iPad',
      features: [
        'Native iOS experience',
        'Optimized for Apple devices',
        'Face ID / Touch ID support',
        'Push notifications'
      ],
      action: 'Download on App Store',
      onClick: handleIOSDownload,
      gradient: 'from-indigo-700 to-purple-800',
      isLive: 'Live on App Store'
    },
    {
      name: 'Android App',
      icon: (
        <svg className="w-16 h-16" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.523 15.341c-.807 0-1.462.656-1.462 1.463s.655 1.462 1.462 1.462c.807 0 1.462-.655 1.462-1.462s-.655-1.463-1.462-1.463zm-11.046 0c-.807 0-1.462.656-1.462 1.463s.655 1.462 1.462 1.462c.807 0 1.462-.655 1.462-1.462s-.655-1.463-1.462-1.463zm11.405-6.472l1.09-1.973a.311.311 0 00-.114-.425.315.315 0 00-.426.115l-1.107 2.006a9.476 9.476 0 00-3.825-.816c-1.378 0-2.683.289-3.825.816L8.568 6.586a.312.312 0 00-.425-.115.31.31 0 00-.114.425l1.09 1.973C6.412 10.342 4.75 12.78 4.75 15.641h14.5c0-2.861-1.662-5.299-4.368-6.772zM8.59 12.15c-.413 0-.75-.337-.75-.75s.337-.75.75-.75.75.337.75.75-.337.75-.75.75zm6.82 0c-.413 0-.75-.337-.75-.75s.337-.75.75-.75.75.337.75.75-.337.75-.75.75z" />
        </svg>
      ),
      description: 'Download for Android devices',
      features: [
        'Native Android experience',
        'Optimized for performance',
        'Fingerprint authentication',
        'Push notifications'
      ],
      action: 'Get it on Google Play',
      onClick: handleAndroidDownload,
      gradient: 'from-green-600 to-emerald-700',
      isLive: 'Live on Google Play'
    }
  ];

  // Fullscreen Image Viewer Component
  const FullscreenViewer = () => {
    if (!fullscreenImage) return null;

    return (
      <div className="fixed inset-0 bg-black/95 z-100 flex items-center justify-center">
        {/* Close Button */}
        <button
          onClick={closeFullscreen}
          className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white p-3 rounded-full transition-all duration-300 hover:scale-110 z-10"
          aria-label="Close fullscreen"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Image Counter */}
        <div className="absolute top-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-4 py-2 rounded-full text-sm font-semibold backdrop-blur-sm z-10">
          {fullscreenIndex + 1} / {fullscreenImages.length}
        </div>

        {/* Previous Button */}
        <button
          onClick={prevFullscreenImage}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-indigo-600 hover:bg-indigo-700 text-white p-4 rounded-full transition-all duration-300 hover:scale-110 z-10"
          aria-label="Previous image"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M15 19l-7-7 7-7" />
          </svg>
        </button>

        {/* Main Image */}
        <div className="max-w-[95vw] max-h-[95vh] flex items-center justify-center">
          <img
            src={fullscreenImage.src}
            alt={fullscreenImage.alt}
            className="max-w-full max-h-[95vh] object-contain"
          />
        </div>

        {/* Next Button */}
        <button
          onClick={nextFullscreenImage}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-indigo-600 hover:bg-indigo-700 text-white p-4 rounded-full transition-all duration-300 hover:scale-110 z-10"
          aria-label="Next image"
        >
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M9 5l7 7-7 7" />
          </svg>
        </button>

        {/* Image Title/Description */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/70 text-white px-6 py-3 rounded-lg backdrop-blur-sm max-w-2xl text-center">
          <p className="font-semibold">{fullscreenImage.alt}</p>
        </div>

        {/* ESC hint */}
        <div className="absolute bottom-4 right-4 bg-black/50 text-white/70 px-3 py-1 rounded text-xs backdrop-blur-sm">
          Press ESC to close
        </div>
      </div>
    );
  };

  // Carousel Component
  const Carousel = ({ images, currentIndex, setCurrentIndex, title }) => {
    const nextSlide = () => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    };

    const prevSlide = () => {
      setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
    };

    const goToSlide = (index) => {
      setCurrentIndex(index);
    };

    return (
      <div className="relative group">
        {/* Title */}
        <div className="text-center mb-6">
          <h3 className="text-2xl font-bold text-white mb-2">{title}</h3>
          <div className="h-1 w-20 bg-linear-to-r from-indigo-600 to-purple-600 mx-auto rounded-full"></div>
        </div>

        {/* Main Image Display */}
        <div className="relative bg-gray-900/50 rounded-2xl overflow-hidden border border-indigo-400/40 shadow-2xl">
          <div 
            className="relative aspect-video cursor-pointer group/image"
            onClick={() => openFullscreen(images, currentIndex)}
          >
            <img
              src={images[currentIndex].src}
              alt={images[currentIndex].alt}
              className="w-full h-full object-contain transition-transform duration-300 group-hover/image:scale-105"
            />
            
            {/* Gradient Overlays for depth */}
            <div className="absolute inset-0 bg-linear-to-t from-black/20 via-transparent to-black/20 pointer-events-none"></div>
            
            {/* Click to expand hint */}
            <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover/image:opacity-100 transition-opacity duration-300 bg-black/30">
              <div className="bg-indigo-600 text-white px-4 py-2 rounded-lg flex items-center gap-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
                <span className="font-semibold">Click to expand</span>
              </div>
            </div>
          </div>

          {/* Navigation Arrows */}
          <button
            onClick={prevSlide}
            className="absolute left-4 top-1/2 -translate-y-1/2 bg-indigo-600/80 hover:bg-indigo-600 text-white p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110"
            aria-label="Previous image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>

          <button
            onClick={nextSlide}
            className="absolute right-4 top-1/2 -translate-y-1/2 bg-indigo-600/80 hover:bg-indigo-600 text-white p-3 rounded-full transition-all duration-300 opacity-0 group-hover:opacity-100 hover:scale-110"
            aria-label="Next image"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>

          {/* Image Counter */}
          <div className="absolute bottom-4 left-4 bg-black/70 text-white px-3 py-1 rounded-full text-sm font-semibold backdrop-blur-sm">
            {currentIndex + 1} / {images.length}
          </div>
        </div>

        {/* Dot Indicators */}
        <div className="flex justify-center gap-2 mt-6 flex-wrap">
          {images.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`transition-all duration-300 rounded-full ${
                index === currentIndex 
                  ? 'bg-indigo-600 w-8 h-3' 
                  : 'bg-indigo-400/40 hover:bg-indigo-400/60 w-3 h-3'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    );
  };

  const PlatformCard = ({ platform }) => (
    <div className="relative group h-full">
      <div className="bg-linear-to-br from-gray-950/90 to-indigo-950/40 border border-indigo-400/90 rounded-2xl p-8 h-full hover:border-indigo-400/60 transition-all duration-300 hover:shadow-2xl hover:shadow-indigo-500/20 flex flex-col">
        {platform.isLive && (
          <div className="absolute top-4 right-4 bg-green-500/90 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" />
            </svg>
            {platform.isLive}
          </div>
        )}

        <div className="text-indigo-300 mb-4">{platform.icon}</div>
        <h3 className="text-2xl font-bold text-white mb-3">{platform.name}</h3>
        <p className="text-indigo-200 text-lg mb-6">{platform.description}</p>

        <ul className="space-y-2 mb-8 grow">
          {platform.features.map((feature, idx) => (
            <li key={idx} className="flex items-start text-indigo-50">
              <svg className="w-5 h-5 text-green-400 mr-2 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span className="text-base">{feature}</span>
            </li>
          ))}
        </ul>

        <button
          onClick={platform.onClick}
          className={`w-full py-3 px-6 rounded-xl font-semibold text-white transition-all duration-300 mt-auto
            bg-linear-to-r ${platform.gradient} hover:shadow-lg hover:scale-105 hover:shadow-indigo-500/50`}
        >
          {platform.action}
        </button>
      </div>
    </div>
  );

  return (
    <>
      <Helmet>
        <title>Get Started with Andlegur | Download App or Login</title>
        <meta
          name="description"
          content="Access Andlegur on your preferred platform. Download our mobile app for iOS and Android, or use the web app directly in your browser."
        />
        <meta
          name="keywords"
          content="Andlegur download, Andlegur app, iOS app, Android app, web app, meditation app download, journaling app, personal growth app"
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${urlConfig.app.currentBaseURL}/get-started`} />
        <meta property="og:title" content="Get Started with Andlegur | Download or Login" />
        <meta
          property="og:description"
          content="Choose your platform and begin your journey with Andlegur today."
        />
        <meta property="og:image" content={urlConfig.assets.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Get Started with Andlegur" />
        <meta
          name="twitter:description"
          content="Download the Andlegur app or access via web browser."
        />
        <meta name="twitter:image" content={urlConfig.assets.ogImage} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={`${urlConfig.app.currentBaseURL}/get-started`} />
      </Helmet>

      {/* Fullscreen Image Viewer */}
      <FullscreenViewer />

      <div className="min-h-screen font-serif text-indigo-100">
        {/* Hero + Platform Cards — single section so web34 spans both */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
          {/* Fused background — web 34 */}
          <div className="absolute inset-0 z-0 pointer-events-none">
            <img
              src="/web_images/Andlegur%20web%2034.webp"
              alt=""
              className="w-full h-full object-cover opacity-25 mix-blend-screen"
              loading="eager"
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-linear-to-b from-[#05091B]/60 via-transparent to-[#05091B]/80" />
            <div className="absolute inset-0 bg-linear-to-r from-[#05091B]/60 via-transparent to-[#05091B]/60" />
          </div>

          {/* Hero text */}
          <div className="relative z-10 max-w-7xl mx-auto text-center mb-16">
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-linear-to-r from-white to-indigo-300 mb-6">
              Choose Your Platform
            </h1>
            <p className="text-xl sm:text-2xl text-indigo-100 mb-4 max-w-3xl mx-auto">
              Access Andlegur wherever you are, on the device you prefer
            </p>
            <p className="text-base sm:text-lg text-white max-w-2xl mx-auto">
              Your journey to reflection and growth starts here. All platforms sync seamlessly.
            </p>
          </div>

          {/* Platform Cards */}
          <div className="relative z-10 max-w-7xl mx-auto pb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {platforms.map((platform, idx) => (
                <PlatformCard key={idx} platform={platform} />
              ))}
            </div>
          </div>
        </section>

        {/* Quick Start Guide Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-linear-to-b from-transparent via-indigo-900/10 to-transparent">
          <div className="max-w-4xl mx-auto">
            <div className="bg-indigo-950/20 border border-indigo-400/90 rounded-2xl p-8 sm:p-12">
              <h2 className="text-3xl font-bold text-white mb-8 text-center">
                Getting Started is Easy
              </h2>

              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="shrink-0 w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                    1
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Choose Your Platform</h3>
                    <p className="text-indigo-100">
                      Select web app for instant access, or download our mobile app for on-the-go reflection.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="shrink-0 w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                    2
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Create Your Account</h3>
                    <p className="text-indigo-100">
                      Sign up with your email. It takes less than 2 minutes.
                    </p>
                  </div>
                </div>

                <div className="flex items-start space-x-4">
                  <div className="shrink-0 w-10 h-10 bg-indigo-600 rounded-full flex items-center justify-center text-white font-bold">
                    3
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-white mb-2">Begin Your Journey</h3>
                    <p className="text-indigo-100">
                      Explore guided practices, journal your thoughts, and connect with a supportive community.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-10 text-center">
                <button
                  onClick={() => navigate('/register')}
                  className="inline-block px-8 py-4 bg-linear-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 hover:shadow-purple-500/50"
                >
                  Create Free Account
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* App Preview Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-linear-to-b from-indigo-900/10 via-purple-900/10 to-indigo-900/10">
          {/* Fused background — web 35 */}
          <div className="absolute inset-0 z-0 pointer-events-none">
            <img
              src="/web_images/Andlegur%20web%2035.webp"
              alt=""
              className="w-full h-full object-cover opacity-20 mix-blend-screen"
              loading="lazy"
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-linear-to-b from-[#05091B]/70 via-transparent to-[#05091B]/70" />
            <div className="absolute inset-0 bg-linear-to-r from-[#05091B]/50 via-transparent to-[#05091B]/50" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto">
            {/* Section Header */}
            <div className="text-center mb-16">
              <h2 className="text-4xl sm:text-5xl font-extrabold text-transparent bg-clip-text bg-linear-to-r from-white to-indigo-300 mb-4">
                See Andlegur in Action
              </h2>
              <p className="text-xl text-indigo-200 max-w-3xl mx-auto">
                Explore the beautiful interface and powerful features across all platforms
              </p>
            </div>

            {/* Carousels Container */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
              {/* Web App Carousel */}
              <div className="bg-indigo-950/30 border border-indigo-400/50 rounded-3xl p-6 sm:p-8 backdrop-blur-sm">
                <Carousel
                  images={webAppImages}
                  currentIndex={webCarouselIndex}
                  setCurrentIndex={setWebCarouselIndex}
                  title="Web Application"
                />
                <div className="mt-6 text-center">
                  <p className="text-indigo-300 mb-4">
                    Full-featured desktop experience with responsive design
                  </p>
                  <button
                    onClick={() => navigate('/login')}
                    className="inline-flex items-center gap-2 px-6 py-3 bg-linear-to-r from-blue-600 to-indigo-700 text-white font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                    </svg>
                    Try Web App
                  </button>
                </div>
              </div>

              {/* Mobile App Carousel */}
              <div className="bg-indigo-950/30 border border-indigo-400/50 rounded-3xl p-6 sm:p-8 backdrop-blur-sm">
                <Carousel
                  images={mobileAppImages}
                  currentIndex={mobileCarouselIndex}
                  setCurrentIndex={setMobileCarouselIndex}
                  title="Mobile Application"
                />
                <div className="mt-6 text-center">
                  <p className="text-indigo-300 mb-4">
                    Native mobile experience optimized for iOS and Android
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                    <button
                      onClick={handleIOSDownload}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-linear-to-r from-indigo-700 to-purple-800 text-white font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                      </svg>
                      Download on App Store
                    </button>
                    <button
                      onClick={handleAndroidDownload}
                      className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-linear-to-r from-green-600 to-emerald-700 text-white font-semibold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M17.523 15.341c-.807 0-1.462.656-1.462 1.463s.655 1.462 1.462 1.462c.807 0 1.462-.655 1.462-1.462s-.655-1.463-1.462-1.463zm-11.046 0c-.807 0-1.462.656-1.462 1.463s.655 1.462 1.462 1.462c.807 0 1.462-.655 1.462-1.462s-.655-1.463-1.462-1.463zm11.405-6.472l1.09-1.973a.311.311 0 00-.114-.425.315.315 0 00-.426.115l-1.107 2.006a9.476 9.476 0 00-3.825-.816c-1.378 0-2.683.289-3.825.816L8.568 6.586a.312.312 0 00-.425-.115.31.31 0 00-.114.425l1.09 1.973C6.412 10.342 4.75 12.78 4.75 15.641h14.5c0-2.861-1.662-5.299-4.368-6.772zM8.59 12.15c-.413 0-.75-.337-.75-.75s.337-.75.75-.75.75.337.75.75-.337.75-.75.75zm6.82 0c-.413 0-.75-.337-.75-.75s.337-.75.75-.75.75.337.75.75-.337.75-.75.75z" />
                      </svg>
                      Get on Google Play
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Feature Highlights */}
            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-indigo-600/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-indigo-400/50">
                  <svg className="w-8 h-8 text-indigo-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                  </svg>
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Intuitive Design</h4>
                <p className="text-indigo-200">Beautiful, user-friendly interface that makes reflection effortless</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-purple-600/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-purple-400/50">
                  <svg className="w-8 h-8 text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Powerful Features</h4>
                <p className="text-indigo-200">Advanced tools for journaling, reflection, and personal growth</p>
              </div>
              
              <div className="text-center">
                <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-4 border border-blue-400/50">
                  <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <h4 className="text-xl font-semibold text-white mb-2">Seamless Sync</h4>
                <p className="text-indigo-200">Your data syncs instantly across all your devices</p>
              </div>
            </div>
          </div>
        </section>

        {/* System Requirements Section */}
        <section className="relative py-16 px-4 sm:px-6 lg:px-8 z-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-8 text-center">
              System Requirements
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-indigo-950/50 border border-indigo-400/50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-8 h-8 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                  </svg>
                  <h3 className="text-xl font-semibold text-white">Web App</h3>
                </div>
                <ul className="space-y-2 text-indigo-100 text-sm">
                  <li>• Chrome 90+</li>
                  <li>• Firefox 88+</li>
                  <li>• Safari 14+</li>
                  <li>• Edge 90+</li>
                  <li>• Stable internet connection</li>
                </ul>
              </div>

              <div className="bg-indigo-950/50 border border-indigo-400/50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-8 h-8 text-gray-300" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                  </svg>
                  <h3 className="text-xl font-semibold text-white">iOS App</h3>
                </div>
                <ul className="space-y-2 text-indigo-100 text-sm">
                  <li>• iOS 14.0 or later</li>
                  <li>• iPhone, iPad, iPod touch</li>
                  <li>• 100 MB free space</li>
                  <li>• Internet for sync</li>
                </ul>
              </div>

              <div className="bg-indigo-950/50 border border-indigo-400/50 rounded-xl p-6">
                <div className="flex items-center gap-3 mb-4">
                  <svg className="w-8 h-8 text-green-400" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.523 15.341c-.807 0-1.462.656-1.462 1.463s.655 1.462 1.462 1.462c.807 0 1.462-.655 1.462-1.462s-.655-1.463-1.462-1.463zm-11.046 0c-.807 0-1.462.656-1.462 1.463s.655 1.462 1.462 1.462c.807 0 1.462-.655 1.462-1.462s-.655-1.463-1.462-1.463zm11.405-6.472l1.09-1.973a.311.311 0 00-.114-.425.315.315 0 00-.426.115l-1.107 2.006a9.476 9.476 0 00-3.825-.816c-1.378 0-2.683.289-3.825.816L8.568 6.586a.312.312 0 00-.425-.115.31.31 0 00-.114.425l1.09 1.973C6.412 10.342 4.75 12.78 4.75 15.641h14.5c0-2.861-1.662-5.299-4.368-6.772zM8.59 12.15c-.413 0-.75-.337-.75-.75s.337-.75.75-.75.75.337.75.75-.337.75-.75.75zm6.82 0c-.413 0-.75-.337-.75-.75s.337-.75.75-.75.75.337.75.75-.337.75-.75.75z" />
                  </svg>
                  <h3 className="text-xl font-semibold text-white">Android App</h3>
                </div>
                <ul className="space-y-2 text-indigo-100 text-sm">
                  <li>• Android 8.0 or later</li>
                  <li>• Google Play Services</li>
                  <li>• 100 MB free space</li>
                  <li>• Internet for sync</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="relative py-16 px-4 sm:px-6 lg:px-8 bg-linear-to-b from-transparent via-gray-900/10 to-transparent z-10">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-white mb-12 text-center">
              Frequently Asked Questions
            </h2>

            <div className="space-y-6">
              <div className="bg-indigo-950/50 border border-indigo-400/50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Do I need to download an app to use Andlegur?
                </h3>
                <p className="text-indigo-100">
                  No! You can use Andlegur directly in your web browser without downloading anything. However, 
                  our mobile apps offer additional features like native notifications and a dedicated mobile experience.
                </p>
              </div>

              <div className="bg-indigo-950/50 border border-indigo-400/50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Will my data sync across devices?
                </h3>
                <p className="text-indigo-100">
                  Yes! Your account syncs seamlessly across all platforms. Start a reflection on your phone 
                  and finish it on your computer—your progress is always saved.
                </p>
              </div>

              <div className="bg-indigo-950/50 border border-indigo-400/50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Is there a cost to download the app?
                </h3>
                <p className="text-indigo-100">
                  The app is free to download. We offer both free and premium membership tiers. 
                  You can explore Andlegur with a free account and upgrade anytime for additional features.
                </p>
              </div>

              <div className="bg-indigo-950/50 border border-indigo-400/50 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-white mb-3">
                  Is the app available on the App Store and Google Play?
                </h3>
                <p className="text-indigo-100">
                  Yes! Andlegur is now live on both the <a href="https://apps.apple.com/us/app/andlegur/id6759088599" target="_blank" rel="noopener noreferrer" className="text-indigo-300 underline hover:text-white">Apple App Store</a> and the <a href="https://play.google.com/store/apps/details?id=com.andlegur.mobile" target="_blank" rel="noopener noreferrer" className="text-green-300 underline hover:text-white">Google Play Store</a>. Download it for free on your iPhone, iPad, or Android device.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Feature Graphic Banner Section */}
        <section className="relative py-16 px-4 sm:px-6 lg:px-8 overflow-hidden">
          {/* Fused background — web 36 */}
          <div className="absolute inset-0 z-0 pointer-events-none">
            <img
              src="/web_images/Andlegur%20web%2036.webp"
              alt=""
              className="w-full h-full object-cover opacity-20 mix-blend-screen"
              loading="lazy"
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-linear-to-b from-[#05091B]/70 via-transparent to-[#05091B]/70" />
            <div className="absolute inset-0 bg-linear-to-r from-[#05091B]/50 via-transparent to-[#05091B]/50" />
          </div>
          <div className="relative z-10 max-w-7xl mx-auto">
              {/* Section Header */}
              <div className="text-center mb-10">
                <h2 className="text-3xl sm:text-4xl font-extrabold text-transparent bg-clip-text bg-linear-to-r from-white to-indigo-300 mb-4">
                  Everything You Need for Personal Growth
                </h2>
                <p className="text-lg text-indigo-200 max-w-2xl mx-auto">
                  Discover the complete suite of features designed to support your journey
                </p>
              </div>

              {/* Feature Graphic — wrapped in figure for SEO indexability */}
              <figure className="relative group" itemScope itemType="https://schema.org/ImageObject">
                <meta itemProp="name" content="Andlegur Features Overview" />
                <meta itemProp="description" content="Complete platform overview: journaling, guided reflection, community support, emotional analytics, archetypes, and personal growth tools for iOS, Android, and Web." />
                <meta itemProp="url" content="https://andlegur.com/Andlegur%20Feature%20Graphic.webp" />
                <div className="absolute inset-0 bg-linear-to-r from-indigo-600/20 to-purple-600/20 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500"></div>
                
                <div className="relative bg-linear-to-br from-indigo-950/40 to-purple-950/40 border border-indigo-400/50 rounded-3xl p-4 sm:p-6 backdrop-blur-sm hover:border-indigo-400/70 transition-all duration-300">
                  <img 
                    src="/Andlegur%20Feature%20Graphic.webp" 
                    alt="Andlegur platform features: journaling, guided reflection, community support, emotional analytics, archetypes, and personal growth tools — available on iOS, Android, and Web" 
                    className="w-full h-auto rounded-2xl shadow-2xl"
                    loading="eager"
                    fetchPriority="high"
                    width="1920"
                    height="1080"
                    itemProp="contentUrl"
                  />
                </div>
                <figcaption className="sr-only">
                  Andlegur — complete platform features including daily journaling, guided reflection practices, community support circles, emotional analytics, archetype discovery, and personal growth tools. Available on iOS, Android, and Web.
                </figcaption>
              </figure>

              {/* CTA Below Image */}
              <div className="text-center mt-10">
                <p className="text-indigo-300 mb-6 text-lg">
                  Experience all these powerful features and more
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <button
                    onClick={() => navigate('/register')}
                    className="px-8 py-4 bg-linear-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:shadow-lg hover:shadow-purple-500/50 hover:scale-105 transition-all duration-300"
                  >
                    Get Started for Free
                  </button>
                  <div className="flex items-center gap-2 text-indigo-200">
                    <svg className="w-5 h-5 text-green-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                    <span className="text-sm">No credit card required</span>
                  </div>
                </div>
              </div>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
          {/* Fused background — web 36 continues */}
          <div className="absolute inset-0 z-0 pointer-events-none">
            <img
              src="/web_images/Andlegur%20web%2036.webp"
              alt=""
              className="w-full h-full object-cover opacity-15 mix-blend-screen"
              loading="lazy"
              aria-hidden="true"
            />
            <div className="absolute inset-0 bg-linear-to-t from-[#05091B] via-transparent to-[#05091B]/80" />
          </div>
          <div className="relative z-10 max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
              Ready to Begin Your Journey?
            </h2>
            
            <p className="text-xl text-indigo-200 mb-10">
              Join thousands finding clarity, peace, and purpose with Andlegur
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/register')}
                className="px-8 py-4 bg-linear-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 hover:shadow-purple-500/50"
              >
                Start Free Today
              </button>
              
              <button
                onClick={() => navigate('/about')}
                className="px-8 py-4 bg-indigo-950/50 border border-indigo-400/80 text-white font-semibold rounded-xl hover:bg-white/20 hover:border-indigo-400/70 transition-all duration-300"
              >
                Learn More
              </button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default GetStarted;
